import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Building2,
  CheckCircle,
  XCircle,
  Users,
  CreditCard,
  Receipt,
  Settings,
  Shield,
  ChevronDown,
  LogOut,
  X,
  HelpCircle,
} from 'lucide-react';

const SIDEBAR_STATE_KEY = 'admin_sidebar_state';
const SIDEBAR_WIDTH_CLASS = 'w-64';

/**
 * ResponsiveAdminSidebar - Premium Design
 * Exactly matches the Owner's ResponsiveSidebar.js styling
 * Colors: Rose/Red accent instead of Teal
 */
const ResponsiveAdminSidebar = ({ isOpen, onClose, isMobile = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Helper to find which group contains current path
  const getGroupForPath = (path) => {
    if (path.includes('/admin/dashboard')) return 'dashboard';
    if (path.includes('/admin/businesses')) return 'businesses';
    if (path.includes('/admin/users')) return 'users';
    if (path.includes('/admin/plans') || path.includes('/admin/subscriptions')) return 'plans';
    if (path.includes('/admin/audit') || path.includes('/admin/settings')) return 'system';
    return 'dashboard';
  };

  const [expandedGroup, setExpandedGroup] = useState(() => {
    return getGroupForPath(location.pathname);
  });

  // Update expanded group when route changes
  useEffect(() => {
    setExpandedGroup(getGroupForPath(location.pathname));
  }, [location.pathname]);

  const menuGroups = [
    {
      key: 'dashboard',
      label: 'Home',
      icon: LayoutDashboard,
      children: [
        { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      ],
    },
    {
      key: 'businesses',
      label: 'Businesses',
      icon: Building2,
      children: [
        { path: '/admin/businesses', label: 'All Businesses', icon: Building2 },
        { path: '/admin/businesses/active', label: 'Active', icon: CheckCircle },
        { path: '/admin/businesses/inactive', label: 'Suspended', icon: XCircle },
      ],
    },
    {
      key: 'users',
      label: 'Users',
      icon: Users,
      children: [
        { path: '/admin/users', label: 'All Users', icon: Users },
      ],
    },
    {
      key: 'plans',
      label: 'Plans & Billing',
      icon: CreditCard,
      children: [
        { path: '/admin/plans-v2', label: 'Plans Management', icon: CreditCard },
        { path: '/admin/menu-editor', label: 'Menu Editor', icon: Settings },
        { path: '/admin/plans', label: 'Plans (Legacy)', icon: CreditCard },
        { path: '/admin/subscriptions', label: 'Subscriptions', icon: Receipt },
      ],
    },
    {
      key: 'system',
      label: 'System',
      icon: Settings,
      children: [
        { path: '/admin/audit-logs', label: 'Audit Logs', icon: Shield },
        { path: '/admin/settings', label: 'System Config', icon: Settings },
      ],
    },
  ];

  const isActive = (path) => {
    if (path === '/admin/dashboard') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const hasActiveChild = (group) => {
    return group.children?.some(item => isActive(item.path));
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroup(expandedGroup === groupKey ? null : groupKey);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <button
        key={item.path}
        onClick={() => handleNavigation(item.path)}
        className={`w-full flex flex-row items-center gap-2.5 pl-8 pr-3 py-2 text-left transition-all duration-200 rounded-md relative ${
          active
            ? 'bg-rose-600/20 text-rose-400'
            : 'text-gray-300 hover:bg-[#2A2D35] hover:text-white'
        }`}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {/* Active indicator line */}
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] bg-rose-500 rounded-r-full" />
        )}
        {Icon && <Icon className={`w-[15px] h-[15px] flex-shrink-0 ${active ? 'text-rose-400' : ''}`} style={{ display: 'block' }} />}
        <span className={`text-[13px] leading-none ${active ? 'font-medium' : ''}`} style={{ display: 'block', lineHeight: '15px' }}>{item.label}</span>
      </button>
    );
  };

  const renderGroup = (group) => {
    const isExpanded = expandedGroup === group.key;
    const GroupIcon = group.icon;
    const groupHasActiveChild = hasActiveChild(group);

    return (
      <div key={group.key} className="mb-1">
        {/* Group Header */}
        <button
          onClick={() => toggleGroup(group.key)}
          className={`w-full flex flex-row items-center justify-between px-3 py-2.5 text-left premium-transition-colors rounded-lg relative ${
            isExpanded
              ? 'bg-[#2A2D35] text-white'
              : groupHasActiveChild
                ? 'bg-[#252830] text-rose-400'
                : 'text-gray-300 hover:bg-[#2A2D35] hover:text-white'
          }`}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {/* Active parent indicator */}
          {(isExpanded || groupHasActiveChild) && (
            <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[70%] rounded-r-full ${
              isExpanded ? 'bg-rose-500' : 'bg-rose-500/50'
            }`} />
          )}
          <div className="flex flex-row items-center gap-2.5 flex-1" style={{ display: 'flex', alignItems: 'center' }}>
            {GroupIcon && <GroupIcon className={`w-[17px] h-[17px] flex-shrink-0 ${groupHasActiveChild ? 'text-rose-400' : ''}`} style={{ display: 'block' }} />}
            <span className={`text-[13px] leading-none ${isExpanded || groupHasActiveChild ? 'font-medium' : ''}`} style={{ display: 'block', lineHeight: '17px' }}>{group.label}</span>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ease-out flex-shrink-0 ml-2 ${
            isExpanded ? 'rotate-0' : '-rotate-90'
          }`} style={{ display: 'block' }} />
        </button>

        {/* Group Content with smooth accordion */}
        <div
          className={`overflow-hidden ${
            isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{
            transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease-in-out'
          }}
        >
          <div className="pl-1 mt-2 pr-1 space-y-0.5">
            {group.children.map((item) => renderMenuItem(item))}
          </div>
        </div>
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[#181A1F] text-white">
      {/* Header - Premium Typography */}
      <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg flex items-center justify-center shadow-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-[15px] tracking-tight">Billsera</h2>
              <p className="text-[10px] text-rose-300 font-medium uppercase tracking-wider">Admin Console</p>
            </div>
          </div>
        </div>
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover:bg-gray-700 rounded flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Admin Info */}
      <div className="px-4 py-3 border-b border-gray-700/50 bg-[#1E2027]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-500/20 flex items-center justify-center">
            <span className="text-[13px] font-bold text-rose-400">
              {user?.name?.[0]?.toUpperCase() || 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium truncate text-white">{user?.name || 'Super Admin'}</div>
            <div className="text-[10px] text-gray-400 truncate">{user?.email}</div>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto py-4 scrollbar-custom">
        {menuGroups.map(renderGroup)}
      </div>

      {/* Fixed Bottom Actions - Premium Typography */}
      <div className="border-t border-gray-700/50 p-2">
        <button
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-gray-400 hover:bg-[#2A2D35] hover:text-gray-200 rounded-lg transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="text-[13px] font-normal">Help & Support</span>
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-red-400/80 hover:bg-red-900/20 hover:text-red-400 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-[13px] font-normal">Logout</span>
        </button>
      </div>
    </div>
  );

  // Desktop only - sidebar with rounded corners
  if (!isMobile) {
    return (
      <div className={`${SIDEBAR_WIDTH_CLASS} h-full rounded-lg shadow-xl overflow-hidden`}>
        {sidebarContent}
      </div>
    );
  }

  // Mobile - return content only (will be wrapped by MobileMenuDrawer)
  return sidebarContent;
};

export default ResponsiveAdminSidebar;
